import type { NavigationGuardNext, RouteRecordNormalized } from "vue-router"
import {$auth} from "@/plugins/auth"
import {ROUTES} from "@/router/paths"
import route from "@/plugins/route"
import {roles} from "@/enums/roles"
import {accessTier} from "@/enums/accessTier"
import {seatSuspended, tierAtLeast} from "@/utils/access"
import {plusEndedPending} from "@/utils/plusEnded"

/**
 * Decides who gets into the app at all.
 */
export default class Subscribed implements App.Middleware.Instance {
  async handle(
    to: RouteRecordNormalized,
    from: RouteRecordNormalized,
    next: App.Middleware.Caller,
    cancel: NavigationGuardNext
  ): Promise<void> {
    // Cursor: Handle unauthenticated users by allowing Authorized middleware to handle redirect
    if (!$auth.user) {
      return next(to, from, cancel)
    }

    const isAdmin =
      $auth.user!.role === roles.LIBRA_ADMIN ||
      $auth.user!.account.original_owner!.role === roles.LIBRA_ADMIN

    // A member the account can no longer cover. Sending them to the payment
    // screen would be a dead end, since only the owner can pay.
    if (!isAdmin && seatSuspended($auth.user)) {
      return cancel(route(ROUTES.SUSPENDED))
    }

    // The one-time notice after a downgrade, before the app rather than on
    // top of it, so nobody wonders mid-task why a feature stopped working.
    if (plusEndedPending()) {
      return cancel(route(ROUTES.PLUS_ENDED))
    }

    // has_active_subscription first and on its own, so a paying customer is
    // never bounced because a payload arrived without the newer tier field.
    if (
      $auth.user!.account!.has_active_subscription ||
      isAdmin ||
      tierAtLeast($auth.user!.account, accessTier.FREE)
    ) {
      return next(to, from, cancel)
    }

    return cancel(route(ROUTES.PAYMENT, {plan: 'annual'}))
  }
}
